import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faGraduationCap, faClipboardList, faBuilding,
  faMoneyBillWave, faReceipt, faPercent, faExclamationTriangle,
  faFileAlt, faClock, faCheckCircle, faSpinner,
  faShoppingCart, faChartLine, faSort, faSortUp, faSortDown,
  faArrowRight, faCalendarAlt, faArrowUp, faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { dashboardAdminService } from '../../services/dashboardAdminService';
import { extensaoService } from '../../services/extensaoService';
import cursoService from '../../services/cursoService';
import Sparkline from './components/Sparkline';
import MultiSelectFilter from './components/MultiSelectFilter';
import SecaoAcademico from './components/SecaoAcademico';
import SecaoFinanceiro from './components/SecaoFinanceiro';
import ModalDetalhesExtensao from './components/ModalDetalhesExtensao';
import EscopoBadge from './components/EscopoBadge';
import InfoTip from './components/InfoTip';

// ============================================================
// Helpers
// ============================================================
const fmtMoeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtNumero = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);
const fmtData = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '∅';
// Extrai YYYY-MM-DD de strings como "2026-04-26 03:00:00" ou ISO.
const isoDate = (v) => v ? String(v).slice(0, 10) : '';

// ============================================================
// Mini-componentes inline
// ============================================================

function DeltaBadge({ delta, loading, refLabel = 'vs período anterior' }) {
  if (loading) return null; // spinner do valor principal já cobre o loading
  if (delta === null || delta === undefined) {
    return <p className="text-xs text-gray-400 italic mt-1">sem dados anteriores</p>;
  }
  const positivo = delta >= 0;
  const corBg = positivo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${corBg}`}>
        <FontAwesomeIcon icon={positivo ? faArrowUp : faArrowDown} className="text-[10px]" />
        {Math.abs(delta).toFixed(1)}%
      </span>
      <span className="text-[11px] text-gray-500">{refLabel}</span>
    </div>
  );
}

function KpiCard({ icone, label, valor, sublabel, cor = 'blue', loading, delta, to, escopo = 'periodo', loadingDelta, refLabel, info }) {
  const corBg = { blue: 'bg-blue-100', green: 'bg-green-100', yellow: 'bg-yellow-100', red: 'bg-red-100', purple: 'bg-purple-100', indigo: 'bg-indigo-100' }[cor];
  const corText = { blue: 'text-blue-600', green: 'text-green-600', yellow: 'text-yellow-600', red: 'text-red-600', purple: 'text-purple-600', indigo: 'text-indigo-600' }[cor];

  const conteudo = (
    <div className={`bg-white rounded-lg shadow p-5 h-full ${to ? 'hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <EscopoBadge escopo={escopo} />
            {info && <InfoTip text={info} position="bottom" />}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1 truncate">
            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" /> : valor}
          </p>
          {delta !== undefined && <DeltaBadge delta={delta} loading={loadingDelta} refLabel={refLabel} />}
          {sublabel && <p className="text-xs text-gray-500 mt-1 truncate">{sublabel}</p>}
        </div>
        <div className={`${corBg} rounded-full p-3 flex-shrink-0`}>
          <FontAwesomeIcon icon={icone} className={`text-xl ${corText}`} />
        </div>
      </div>
    </div>
  );

  return to ? <Link to={to}>{conteudo}</Link> : conteudo;
}

function AlertaChip({ icone, label, qtd, cor, to }) {
  if (!qtd || qtd === 0) return null;
  const classes = {
    red: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100',
    orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
  }[cor];
  const Component = to ? Link : 'div';
  const props = to ? { to } : {};
  return (
    <Component {...props} className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium ${classes} transition-colors`}>
      <FontAwesomeIcon icon={icone} />
      <span>{label}:</span>
      <span className="font-bold">{fmtNumero(qtd)}</span>
    </Component>
  );
}

function StatusBadgePedido({ status, pago }) {
  if (!status) return <span className="text-gray-400">∅</span>;
  const map = {
    'Concluido': 'bg-green-100 text-green-700',
    'Pendente':  'bg-yellow-100 text-yellow-700',
    'Cancelado': 'bg-red-100 text-red-700',
    'Rascunho':  'bg-gray-100 text-gray-700',
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
      {pago && <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-xs" title="Pago" />}
    </span>
  );
}

function StatusBadgeMatricula({ status }) {
  const map = {
    'Ativa':     'bg-green-100 text-green-700',
    'Concluida': 'bg-blue-100 text-blue-700',
    'Trancada':  'bg-yellow-100 text-yellow-700',
    'Cancelada': 'bg-red-100 text-red-700',
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
}

// ============================================================
// Página principal
// ============================================================

const PERIODOS = [
  { value: 'hoje', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'ano', label: 'Ano' },
];

const DashboardAdmin = () => {
  // Filtros
  const [periodo, setPeriodo] = useState('30d');
  const [extensoesSel, setExtensoesSel] = useState([]); // IDs
  const [cursosSel, setCursosSel] = useState([]); // IDs (no MVP V2 não filtramos endpoints, mas o UI já está pronto)
  // Comparativo de KPIs com período anterior.
  // 'off'           → não compara, não mostra delta
  // 'imediato'      → mesma duração imediatamente antes (tendência recente)
  // 'ano_anterior'  → mesmo intervalo do ano anterior (sazonalidade)
  const [tipoComparacao, setTipoComparacao] = useState('off');
  const comparar = tipoComparacao !== 'off';

  // Opções para filtros
  const [extensoesOpcoes, setExtensoesOpcoes] = useState([]);
  const [cursosOpcoes, setCursosOpcoes] = useState([]);

  // Dados
  const [alertas, setAlertas] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [recentes, setRecentes] = useState(null);
  const [academico, setAcademico] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);

  const [loadingAlertas, setLoadingAlertas] = useState(true);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingRanking, setLoadingRanking] = useState(true);
  const [loadingRecentes, setLoadingRecentes] = useState(true);
  const [loadingAcademico, setLoadingAcademico] = useState(true);
  const [loadingFinanceiro, setLoadingFinanceiro] = useState(true);

  const [erro, setErro] = useState(null);

  // Ordenação e paginação do ranking
  const [ordenarPor, setOrdenarPor] = useState('receita_periodo');
  const [ordem, setOrdem] = useState('desc');
  const [page, setPage] = useState(1);
  const limit = 25;

  // Drill-down: modal de detalhes de uma extensão
  const [extensaoDetalhe, setExtensaoDetalhe] = useState(null);

  // ============================================================
  // Carregar opções dos filtros (uma vez)
  // ============================================================
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const [extResp, cursoResp] = await Promise.all([
          extensaoService.list({ limit: 500 }).catch(() => null),
          cursoService.list({ limit: 500 }).catch(() => null),
        ]);
        const extData = extResp?.data?.extensoes || extResp?.extensoes || extResp?.data || [];
        const cursoData = cursoResp?.data?.cursos || cursoResp?.cursos || cursoResp?.data || [];
        setExtensoesOpcoes(
          (Array.isArray(extData) ? extData : []).map(e => ({
            id: e.idExtensao ?? e.id,
            nome: e.nome,
          })).filter(e => e.id && e.nome).sort((a, b) => a.nome.localeCompare(b.nome))
        );
        setCursosOpcoes(
          (Array.isArray(cursoData) ? cursoData : []).map(c => ({
            id: c.idCurso ?? c.id,
            nome: c.nome,
          })).filter(c => c.id && c.nome).sort((a, b) => a.nome.localeCompare(b.nome))
        );
      } catch (e) {
        // Ignorar — filtros ficam vazios mas o resto do dashboard segue funcionando
        console.warn('Não foi possível carregar opções de filtro:', e?.message);
      }
    })();
    return () => ac.abort();
  }, []);

  // ============================================================
  // Loaders dos endpoints
  // ============================================================
  const carregarAlertas = useCallback(async (signal) => {
    try {
      setLoadingAlertas(true);
      const resp = await dashboardAdminService.alertas({ signal });
      setAlertas(resp.data);
    } catch (e) {
      if (e.name !== 'CanceledError') console.error('Erro alertas:', e);
    } finally {
      setLoadingAlertas(false);
    }
  }, []);

  const carregarKpis = useCallback(async (signal, periodoLocal, extensoesLocal, compararLocal, tipoCompLocal) => {
    try {
      setLoadingKpis(true);
      const resp = await dashboardAdminService.kpis({
        periodo: periodoLocal,
        extensoes: extensoesLocal,
        compararPeriodoAnterior: compararLocal,
        tipoComparacao: tipoCompLocal,
        signal,
      });
      setKpis(resp.data);
    } catch (e) {
      if (e.name !== 'CanceledError') {
        console.error('Erro kpis:', e);
        setErro(e.response?.data?.message || 'Erro ao carregar KPIs');
      }
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  const carregarRanking = useCallback(async (signal, periodoLocal, extensoesLocal, pageLocal, ordenarPorLocal, ordemLocal) => {
    try {
      setLoadingRanking(true);
      const resp = await dashboardAdminService.extensoesRanking({
        periodo: periodoLocal,
        extensoes: extensoesLocal,
        ordenarPor: ordenarPorLocal,
        ordem: ordemLocal,
        page: pageLocal,
        limit,
        sparkline: true,
        signal,
      });
      setRanking(resp);
    } catch (e) {
      if (e.name !== 'CanceledError') console.error('Erro ranking:', e);
    } finally {
      setLoadingRanking(false);
    }
  }, []);

  const carregarRecentes = useCallback(async (signal) => {
    try {
      setLoadingRecentes(true);
      const resp = await dashboardAdminService.recentes({ signal });
      setRecentes(resp.data);
    } catch (e) {
      if (e.name !== 'CanceledError') console.error('Erro recentes:', e);
    } finally {
      setLoadingRecentes(false);
    }
  }, []);

  const carregarAcademico = useCallback(async (signal, extensoesLocal) => {
    try {
      setLoadingAcademico(true);
      const resp = await dashboardAdminService.academico({ extensoes: extensoesLocal, signal });
      setAcademico(resp.data);
    } catch (e) {
      if (e.name !== 'CanceledError') console.error('Erro academico:', e);
    } finally {
      setLoadingAcademico(false);
    }
  }, []);

  const carregarFinanceiro = useCallback(async (signal, periodoLocal, extensoesLocal) => {
    try {
      setLoadingFinanceiro(true);
      const resp = await dashboardAdminService.financeiro({
        periodo: periodoLocal,
        extensoes: extensoesLocal,
        signal,
      });
      setFinanceiro(resp.data);
    } catch (e) {
      if (e.name !== 'CanceledError') console.error('Erro financeiro:', e);
    } finally {
      setLoadingFinanceiro(false);
    }
  }, []);

  // Reage a período + extensão + toggle de comparação
  useEffect(() => {
    const ac = new AbortController();
    carregarAlertas(ac.signal);
    carregarKpis(ac.signal, periodo, extensoesSel, comparar, tipoComparacao);
    carregarRecentes(ac.signal);
    carregarAcademico(ac.signal, extensoesSel);
    carregarFinanceiro(ac.signal, periodo, extensoesSel);
    return () => ac.abort();
  }, [periodo, extensoesSel, comparar, tipoComparacao, carregarAlertas, carregarKpis, carregarRecentes, carregarAcademico, carregarFinanceiro]);

  // Ranking reage também a ordenação/paginação
  useEffect(() => {
    const ac = new AbortController();
    carregarRanking(ac.signal, periodo, extensoesSel, page, ordenarPor, ordem);
    return () => ac.abort();
  }, [periodo, extensoesSel, page, ordenarPor, ordem, carregarRanking]);

  // ============================================================
  // Handlers
  // ============================================================
  const handleOrdenar = (campo) => {
    if (ordenarPor === campo) {
      setOrdem(ordem === 'desc' ? 'asc' : 'desc');
    } else {
      setOrdenarPor(campo);
      setOrdem('desc');
    }
    setPage(1);
  };

  const iconeOrdem = (campo) => {
    if (ordenarPor !== campo) return faSort;
    return ordem === 'desc' ? faSortDown : faSortUp;
  };

  // ============================================================
  // Render
  // ============================================================
  if (erro) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-3" />
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar dashboard</h3>
          <p className="text-red-600 text-sm">{erro}</p>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const deltaOf = (campo) => kpis?.delta_pct?.[campo];
  // Rótulo do delta nos KPIs: identifica qual referência está em uso.
  const refLabelDelta = tipoComparacao === 'ano_anterior' ? 'vs mesmo período do ano anterior' : 'vs período anterior';

  // Querystring para drill-down em telas que aceitam dataInicio/dataFim
  // (ex.: MatriculasList). Usa o mesmo período do dashboard, formato YYYY-MM-DD.
  const periodoQS = kpis?.periodo
    ? `?dataInicio=${isoDate(kpis.periodo.dataInicio)}&dataFim=${isoDate(kpis.periodo.dataFim)}`
    : '';

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Cabeçalho com filtros */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow p-5 text-white">
        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-2xl font-bold">Dashboard Administrativo</h1>
            <p className="text-indigo-100 text-sm mt-1">Visão consolidada de todas as extensões</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-100" />
              <select
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                className="bg-white/10 border border-white/30 rounded-md px-3 py-1.5 text-white text-sm focus:bg-white/20 focus:outline-none"
              >
                {PERIODOS.map(p => <option key={p.value} value={p.value} className="text-gray-900">{p.label}</option>)}
              </select>
            </div>
            <MultiSelectFilter
              label="Extensões"
              options={extensoesOpcoes}
              value={extensoesSel}
              onChange={setExtensoesSel}
              placeholderTodos={`Todas (${extensoesOpcoes.length})`}
              searchPlaceholder="Buscar extensão…"
            />
            <MultiSelectFilter
              label="Cursos"
              options={cursosOpcoes}
              value={cursosSel}
              onChange={setCursosSel}
              placeholderTodos={`Todos (${cursosOpcoes.length})`}
              searchPlaceholder="Buscar curso…"
            />
            <div
              className="flex items-center gap-1 text-sm bg-white/10 border border-white/30 rounded-md px-1 py-0.5 ml-2"
              title="Mostra a variação % nos 4 KPIs que dependem de período (matrículas, pedidos, receita, ticket médio)"
            >
              <span className="text-indigo-100 text-xs pl-2 pr-1">Comparar:</span>
              {[
                { value: 'off',           label: 'Não',                 title: 'Sem comparativo' },
                { value: 'imediato',      label: 'Período anterior',    title: 'Mesma duração imediatamente antes (ex.: últimos 30 dias vs 30 dias anteriores) — bom para ver tendência recente' },
                { value: 'ano_anterior',  label: 'Ano anterior',        title: 'Mesmo intervalo no ano passado (ex.: 26/abr→25/mai 2026 vs 26/abr→25/mai 2025) — bom para ver sazonalidade' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTipoComparacao(opt.value)}
                  title={opt.title}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    tipoComparacao === opt.value
                      ? 'bg-white text-indigo-700 font-semibold'
                      : 'text-white hover:bg-white/15'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Faixa de alertas */}
      {!loadingAlertas && alertas && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Alertas críticos</span>
            <EscopoBadge escopo="fixo" label="independem do filtro" />
          </div>
          <div className="flex flex-wrap gap-2">
            <AlertaChip icone={faFileAlt} label="Documentos aguardando >7d" qtd={alertas.documentos_pendentes_revisao} cor="yellow" to="/alunos/documentos-pendentes" />
            <AlertaChip icone={faClock} label="Pedidos pendentes >30d" qtd={alertas.pedidos_pendentes_antigos} cor="red" to="/pedidos?status=Pendente" />
            <AlertaChip icone={faShoppingCart} label="Pedidos pagos não concluídos" qtd={alertas.pedidos_pagos_nao_concluidos} cor="orange" to="/pedidos?pagoNaoConcluido=true" />
            <AlertaChip icone={faMoneyBillWave} label="Contas vencidas" qtd={alertas.contas_a_pagar_vencidas} cor="red" to="/financeiro/contas-pagar?pago=Não" />
            <AlertaChip icone={faUsers} label="Alunos sem matrícula ativa" qtd={alertas.alunos_sem_matricula_ativa} cor="yellow" to="/alunos?semMatriculaAtiva=true" />
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icone={faUsers} label="Alunos ativos" valor={kpis ? fmtNumero(kpis.alunos_ativos) : '—'} sublabel="total atual" cor="blue" loading={loadingKpis} to="/alunos?status=Ativo" escopo="extensao"
          info="Pessoas cadastradas como aluno (dtype='Aluno') com status='Ativo'. É o total atual — não considera o filtro de período. Filtro de extensão se aplica." />
        <KpiCard icone={faGraduationCap} label="Novas matrículas" valor={kpis ? fmtNumero(kpis.novas_matriculas_periodo) : '—'} sublabel="no período" cor="indigo" loading={loadingKpis} delta={comparar ? deltaOf('novas_matriculas_periodo') : undefined} loadingDelta={comparar && loadingKpis} refLabel={refLabelDelta} to={`/matriculas${periodoQS}`} escopo="periodo"
          info="Matrículas com data de matrícula dentro do período selecionado. Cada matrícula conta uma vez — uma pessoa pode aparecer mais de uma vez se fez mais de uma matrícula no período." />
        <KpiCard icone={faClipboardList} label="Pedidos" valor={kpis ? fmtNumero(kpis.pedidos_periodo?.total) : '—'} sublabel="no período" cor="purple" loading={loadingKpis} delta={comparar ? deltaOf('pedidos_periodo') : undefined} loadingDelta={comparar && loadingKpis} refLabel={refLabelDelta} to={`/pedidos${periodoQS}`} escopo="periodo"
          info="Pedidos cadastrados dentro do período selecionado, em qualquer status (Pendente, Concluído, Cancelado ou Rascunho). Filtro de extensão se aplica." />
        <KpiCard icone={faMoneyBillWave} label="Receita líquida" valor={kpis ? fmtMoeda(kpis.receita_liquida_periodo) : '—'} sublabel="no período" cor="green" loading={loadingKpis} delta={comparar ? deltaOf('receita_liquida_periodo') : undefined} loadingDelta={comparar && loadingKpis} refLabel={refLabelDelta} to={kpis ? `/financeiro/receitas?dataBalancoInicio=${isoDate(kpis.periodo.dataInicio)}&dataBalancoFim=${isoDate(kpis.periodo.dataFim)}` : '/financeiro/receitas'} escopo="periodo"
          info="Soma dos valores das entradas financeiras com dataBalanço dentro do período. Quando há filtro de extensão, inclui as entradas vinculadas a pedidos das extensões selecionadas E as entradas sem vínculo com pedido (avulsas)." />
        <KpiCard icone={faReceipt} label="Ticket médio" valor={kpis ? fmtMoeda(kpis.ticket_medio) : '—'} sublabel="no período" cor="green" loading={loadingKpis} delta={comparar ? deltaOf('ticket_medio') : undefined} loadingDelta={comparar && loadingKpis} refLabel={refLabelDelta} escopo="periodo"
          info="Valor médio por pedido no período. Cálculo: soma do valor de todos os pedidos no período ÷ quantidade de pedidos. Considera o valor cadastrado no pedido (não a receita efetivamente recebida)." />
        <KpiCard icone={faBuilding} label="Extensões ativas" valor={kpis ? fmtNumero(kpis.extensoes_ativas) : '—'} sublabel="total atual" cor="blue" loading={loadingKpis} to="/extensoes?status=Ativa" escopo="geral"
          info="Quantidade de extensões com status 'Ativa' no momento. Snapshot atual — não muda com nenhum filtro. Quando o filtro de extensão está aplicado, conta apenas as extensões selecionadas que também estão ativas." />
        <KpiCard icone={faPercent} label="Taxa de conclusão" valor={kpis ? `${kpis.taxa_conclusao_pct?.toFixed(1)}%` : '—'} sublabel="histórico total · concluídas / total" cor="indigo" loading={loadingKpis} escopo="extensao"
          info="Percentual de matrículas no status 'Concluida' sobre o total de matrículas (em qualquer status). Considera todo o histórico, não apenas o período. Filtro de extensão se aplica." />
        <KpiCard icone={faExclamationTriangle} label="Inadimplência" valor={kpis ? fmtMoeda(kpis.inadimplencia_valor) : '—'} sublabel="pedidos sem data de pagamento, >30 dias" cor="red" loading={loadingKpis} to="/pedidos?inadimplente=true" escopo="extensao"
          info="Soma do valor de pedidos que (1) não têm data de pagamento (pagoEm IS NULL), (2) não foram cancelados nem estão como rascunho, e (3) foram cadastrados há mais de 30 dias. Mede o que está em aberto há tempo demais." />
      </div>

      {/* Seção Acadêmico (V2) */}
      <SecaoAcademico data={academico} loading={loadingAcademico} />

      {/* Seção Financeiro (V2) */}
      <SecaoFinanceiro data={financeiro} loading={loadingFinanceiro} />

      {/* Ranking de extensões */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} className="text-indigo-600" />
            <h2 className="font-semibold text-gray-900">Ranking de extensões</h2>
            <EscopoBadge escopo="periodo" />
          </div>
          {ranking?.pagination && (
            <span className="text-xs text-gray-500">
              {ranking.pagination.total} extensões — página {ranking.pagination.page} de {ranking.pagination.totalPages}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                {[
                  { campo: 'nome', label: 'Extensão' },
                  { campo: 'alunos_ativos', label: 'Alunos' },
                  { campo: 'matriculas_periodo', label: 'Matr.' },
                  { campo: 'pedidos_periodo', label: 'Pedidos' },
                  { campo: 'receita_periodo', label: 'Receita' },
                  { campo: 'inadimplencia', label: 'Inadimpl.' },
                  { campo: 'pct_conclusao', label: '% concl.' },
                ].map(col => (
                  <th key={col.campo} onClick={() => handleOrdenar(col.campo)} className="px-4 py-3 text-left cursor-pointer select-none hover:bg-gray-100">
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      <FontAwesomeIcon icon={iconeOrdem(col.campo)} className="text-gray-400 text-xs" />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left">Matr. 12m</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingRanking && (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-400">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…
                </td></tr>
              )}
              {!loadingRanking && ranking?.data?.length === 0 && (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-400">Nenhuma extensão encontrada</td></tr>
              )}
              {!loadingRanking && ranking?.data?.map(ext => (
                <tr
                  key={ext.id}
                  className="hover:bg-indigo-50 cursor-pointer"
                  onClick={() => setExtensaoDetalhe(ext.id)}
                  title="Clique para ver detalhes"
                >
                  <td className="px-4 py-2 font-medium text-gray-900">{ext.nome}</td>
                  <td className="px-4 py-2 text-gray-700">{fmtNumero(ext.alunos_ativos)}</td>
                  <td className="px-4 py-2 text-gray-700">{fmtNumero(ext.matriculas_periodo)}</td>
                  <td className="px-4 py-2 text-gray-700">{fmtNumero(ext.pedidos_periodo)}</td>
                  <td className="px-4 py-2 text-gray-900 font-medium">{fmtMoeda(ext.receita_periodo)}</td>
                  <td className={`px-4 py-2 ${ext.inadimplencia > 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmtMoeda(ext.inadimplencia)}</td>
                  <td className="px-4 py-2 text-gray-700">{ext.pct_conclusao}%</td>
                  <td className="px-4 py-2">
                    {ext.sparkline_matriculas_12m && <Sparkline data={ext.sparkline_matriculas_12m} color="#4f46e5" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ranking?.pagination?.totalPages > 1 && (
          <div className="px-5 py-3 border-t flex items-center justify-between text-sm">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 border rounded-md disabled:opacity-50 hover:bg-gray-50">← Anterior</button>
            <span className="text-gray-600">Página {page} de {ranking.pagination.totalPages}</span>
            <button onClick={() => setPage(p => Math.min(ranking.pagination.totalPages, p + 1))} disabled={page >= ranking.pagination.totalPages} className="px-3 py-1.5 border rounded-md disabled:opacity-50 hover:bg-gray-50">Próxima →</button>
          </div>
        )}
      </div>

      {/* Atividade recente */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <FontAwesomeIcon icon={faShoppingCart} className="text-purple-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Últimos pedidos</h3>
            <EscopoBadge escopo="fixo" label="últimos 10" />
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loadingRecentes && <div className="px-5 py-6 text-center text-gray-400 text-sm"><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…</div>}
            {!loadingRecentes && recentes?.pedidos_recentes?.map(p => (
              <div key={p.id} className="px-5 py-3 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.pessoa_nome || 'Sem nome'}</p>
                    <p className="text-xs text-gray-500 truncate">{p.extensao_nome || '∅'} • {fmtData(p.cadastradoEm)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">{fmtMoeda(p.valor)}</p>
                    <StatusBadgePedido status={p.status} pago={p.pago} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <FontAwesomeIcon icon={faGraduationCap} className="text-indigo-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Últimas matrículas</h3>
            <EscopoBadge escopo="fixo" label="últimas 10" />
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loadingRecentes && <div className="px-5 py-6 text-center text-gray-400 text-sm"><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…</div>}
            {!loadingRecentes && recentes?.matriculas_recentes?.map(m => (
              <div key={m.id} className="px-5 py-3 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{m.aluno_nome}</p>
                    <p className="text-xs text-gray-500 truncate">{m.curso_nome} • {m.extensao_nome || '∅'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{fmtData(m.dataMatricula)}</p>
                    <StatusBadgeMatricula status={m.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow" id="ultimos-alunos">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Últimos alunos cadastrados</h3>
            <EscopoBadge escopo="fixo" label="últimos 10" />
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loadingRecentes && <div className="px-5 py-6 text-center text-gray-400 text-sm"><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…</div>}
            {!loadingRecentes && recentes?.alunos_recentes?.map(a => (
              <Link key={a.id} to={`/alunos/${a.id}/editar`} className="block px-5 py-3 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{a.nome}</p>
                    <p className="text-xs text-gray-500 truncate">{a.extensao_nome || '∅'} • {a.email || 'sem e-mail'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{fmtData(a.cadastradoEm)}</p>
                    <FontAwesomeIcon icon={faArrowRight} className="text-gray-300 mt-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de drill-down (V3) */}
      {extensaoDetalhe && (
        <ModalDetalhesExtensao
          extensaoId={extensaoDetalhe}
          periodo={periodo}
          onClose={() => setExtensaoDetalhe(null)}
        />
      )}
    </div>
  );
};

export default DashboardAdmin;
